// src/sections/EarningsSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../components/Overview.css";
import { API_BASE, apiUrl } from "../apiBase";

/** Rename helper so it can NEVER conflict with local variables */
function fmtYMD(d) {
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeekMonday(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun ... 6 Sat
  const diff = (day === 0 ? -6 : 1) - day; // move to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function shortDowLabel(ymd) {
  const d = new Date(`${ymd}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function toNumberOrNull(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * IMPORTANT:
 * FMP sometimes returns 0 for "actual" fields when it's actually missing.
 * We treat 0 as "missing" for actuals ONLY (eps, revenue) to avoid the scary -100% surprises.
 */
function actualOrNull(v) {
  const n = toNumberOrNull(v);
  if (n === null) return null;
  if (n === 0) return null; // treat as missing actual
  return n;
}

function estOrNull(v) {
  // estimates can legitimately be 0 sometimes, but it's rare.
  // We'll keep estimates as-is.
  return toNumberOrNull(v);
}

function formatEPS(x) {
  const n = toNumberOrNull(x);
  if (!Number.isFinite(n)) return "N/A";
  return n.toFixed(2);
}

function formatMoneyCompact(x) {
  const n = toNumberOrNull(x);
  if (!Number.isFinite(n)) return "N/A";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(n);
}

function formatMoneySignedCompact(x) {
  const n = toNumberOrNull(x);
  if (!Number.isFinite(n)) return "N/A";
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const pretty = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(abs);
  return `${sign}${pretty}`;
}

function sessionLabel(session) {
  if (session === "pre") return "Before Open";
  if (session === "post") return "After Close";
  return "Time TBD";
}

function sessionEmoji(session) {
  if (session === "pre") return "🌞";
  if (session === "post") return "🌙";
  return "⏳";
}

function getLogoUrl(symbol) {
  if (!symbol) return null;
  return `${API_BASE}/api/logo/${encodeURIComponent(symbol)}`;
}

function calcSurprise(actual, est) {
  const a = toNumberOrNull(actual);
  const e = toNumberOrNull(est);
  if (!Number.isFinite(a) || !Number.isFinite(e) || e === 0) return null;
  const diff = a - e;
  const pct = diff / Math.abs(e);
  return { diff, pct };
}

function chipInfoFor(actual, est, kindLabel) {
  const a = toNumberOrNull(actual);
  const e = toNumberOrNull(est);

  // If no estimate, we can't classify beat/miss
  if (!Number.isFinite(e)) {
    return {
      text: `No ${kindLabel} est`,
      tone: "neutral",
      show: true,
    };
  }

  // If no actual yet, show as neutral (upcoming)
  if (!Number.isFinite(a)) {
    return {
      text: `No ${kindLabel} actual yet`,
      tone: "neutral",
      show: true,
    };
  }

  if (a > e) return { text: `${kindLabel} Beat`, tone: "good", show: true };
  if (a < e) return { text: `${kindLabel} Miss`, tone: "bad", show: true };
  return { text: `${kindLabel} Inline`, tone: "neutral", show: true };
}

const EarningsSection = ({ onConnectionStatus }) => {
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const [activeModal, setActiveModal] = useState(null);

  // Only allow 3 weeks total:
  // 0 = this week, 1 = next, 2 = week after next
  const [weekOffset, setWeekOffset] = useState(0);
  const MIN_WEEK = 0;
  const MAX_WEEK = 2;

  // Build the viewed week (Mon–Fri)
  const { weekStart, weekDays, fromYMD, toYMDStr } = useMemo(() => {
    const baseMon = startOfWeekMonday(new Date());
    const mon = addDays(baseMon, weekOffset * 7);
    const days = [];
    for (let i = 0; i < 5; i++) days.push(fmtYMD(addDays(mon, i)));
    return {
      weekStart: mon,
      weekDays: days,
      fromYMD: days[0],
      toYMDStr: days[4],
    };
  }, [weekOffset]);

  const fetchCalendar = async () => {
    try {
      setLoading(true);
      setError(null);

      const qs = new URLSearchParams();
      qs.set("from", fromYMD);
      qs.set("to", toYMDStr);

      const res = await fetch(apiUrl(`/api/earnings/calendar?${qs.toString()}`));
      if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
      const json = await res.json();

      setAsOf(json.asOf || null);
      setRows(Array.isArray(json.results) ? json.results : []);

      onConnectionStatus?.(true);
    } catch (e) {
      setError(e?.message || "Failed to load earnings calendar");
      setRows([]);
      setAsOf(null);
      onConnectionStatus?.(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromYMD, toYMDStr]);

  // Group rows into { [date]: { pre:[], post:[], tbd:[] } }
  const byDate = useMemo(() => {
    const map = new Map();
    for (const ymd of weekDays) {
      map.set(ymd, { pre: [], post: [], tbd: [] });
    }

    for (const r of rows) {
      const date = r.date || r.earningsDate || r.reportDate;
      const sym = String(r.symbol || "").toUpperCase();
      if (!date || !map.has(date) || !sym) continue;

      const s = r.session === "pre" ? "pre" : r.session === "post" ? "post" : "tbd";
      map.get(date)[s].push({ ...r, symbol: sym });
    }

    for (const [k, v] of map.entries()) {
      v.pre.sort((a, b) => (a.symbol || "").localeCompare(b.symbol || ""));
      v.post.sort((a, b) => (a.symbol || "").localeCompare(b.symbol || ""));
      v.tbd.sort((a, b) => (a.symbol || "").localeCompare(b.symbol || ""));
      map.set(k, v);
    }

    return map;
  }, [rows, weekDays]);

  const bannerTitle = error ? "Earnings Calendar: Disconnected" : "Earnings Calendar";
  const bannerText = error
    ? `Could not load earnings calendar. (${error})`
    : `Showing ${fromYMD} to ${toYMDStr}.${asOf ? ` (As of ${new Date(asOf).toLocaleString()})` : ""}`;

  if (loading) {
    return (
      <div className="overview-loading">
        <div className="loading-spinner" />
        <p>Loading earnings calendar...</p>
      </div>
    );
  }

  const Tile = ({ row }) => {
    const sym = String(row.symbol || "—").toUpperCase();
    const logoUrl = getLogoUrl(sym);

    return (
      <button
        type="button"
        className="earnings-tile2"
        onClick={() => setActiveModal(row)}
        title={sym}
      >
        <div className="earnings-tile2-inner">
          <div className="earnings-logo-wrap2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={sym}
                className="earnings-logo2"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="earnings-logo-fallback2">•</div>
            )}
          </div>
          <div className="earnings-ticker2">{sym}</div>
        </div>
      </button>
    );
  };

  const Bucket = ({ title, toneClass, icon, items }) => (
    <div className={`earnings-bucket2 ${toneClass}`}>
      <div className="earnings-bucket2-head">
        <div className="earnings-bucket2-left">
          <span>{icon}</span>
          <span className="earnings-bucket2-title">{title}</span>
        </div>
        <div className="earnings-bucket2-count">{items.length}</div>
      </div>

      <div className="earnings-bucket2-grid">
        {items.length ? (
          items.map((r, i) => <Tile key={`${r.symbol}-${title}-${i}`} row={r} />)
        ) : (
          <div className="earnings-empty2">N/A</div>
        )}
      </div>
    </div>
  );

  const DayColumn = ({ ymd }) => {
    const buckets = byDate.get(ymd) || { pre: [], post: [], tbd: [] };
    const total = buckets.pre.length + buckets.post.length + buckets.tbd.length;

    return (
      <div className="earnings-day2">
        <div className="earnings-day2-head">
          <div className="earnings-day2-title">{shortDowLabel(ymd)}</div>
          <div className="earnings-day2-sub">{total} reports</div>
        </div>

        <div className="earnings-day2-body">
          <Bucket title="Before Open" icon="🌞" toneClass="tone-pre" items={buckets.pre} />
          <Bucket title="After Close" icon="🌙" toneClass="tone-post" items={buckets.post} />
          <Bucket title="Time TBD" icon="⏳" toneClass="tone-tbd" items={buckets.tbd} />
        </div>
      </div>
    );
  };

  const Modal = ({ row }) => {
    if (!row) return null;

    const sym = String(row.symbol || "—").toUpperCase();
    const date = row.date || row.earningsDate || row.reportDate || "—";
    const sess = row.session || "tbd";

    // Treat 0 actuals as missing
    const epsAct = actualOrNull(row.eps);
    const epsEst = estOrNull(row.epsEstimated);

    const revAct = actualOrNull(row.revenue);
    const revEst = estOrNull(row.revenueEstimated);

    const epsSur = calcSurprise(epsAct, epsEst);
    const revSur = calcSurprise(revAct, revEst);

    const epsChip = chipInfoFor(epsAct, epsEst, "EPS");
    const revChip = chipInfoFor(revAct, revEst, "Rev");

    const dataIncomplete =
      !(Number.isFinite(toNumberOrNull(epsAct)) && Number.isFinite(toNumberOrNull(epsEst))) ||
      !(Number.isFinite(toNumberOrNull(revAct)) && Number.isFinite(toNumberOrNull(revEst)));

    const headerToneClass =
      sess === "pre"
        ? "earn-modalTone-pre"
        : sess === "post"
        ? "earn-modalTone-post"
        : "earn-modalTone-tbd";

    const logoUrl = getLogoUrl(sym);

    return (
      <div className="earn-modalBackdrop" onClick={() => setActiveModal(null)}>
        <div className={`earn-modalCompany ${headerToneClass}`} onClick={(e) => e.stopPropagation()}>
          {/* Top header */}
          <div className="earn-modalHeader">
            <div className="earn-modalHeaderLeft">
              <div className="earn-modalLogoWrap">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={sym}
                    className="earn-modalLogo"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="earn-modalLogoFallback">•</div>
                )}
              </div>

              <div className="earn-modalTitleBlock">
                <div className="earn-modalTicker">{sym}</div>
                {/* Removed "Company name (coming soon)" on purpose */}
              </div>
            </div>

            <div className="earn-modalHeaderRight">
              <div className="earn-modalDate">{date}</div>
              <button className="earn-modalX" onClick={() => setActiveModal(null)} aria-label="Close">
                ×
              </button>
            </div>
          </div>

          {/* Pills row: session left, chips right */}
          <div className="earn-modalPillsRow">
            <div className="earn-pill earn-pill--session">
              <span className="earn-pillEmoji">{sessionEmoji(sess)}</span>
              <span>{sessionLabel(sess)}</span>
            </div>

            <div className="earn-pillRight">
              {epsChip?.show && (
                <div
                  className={`earn-pill ${
                    epsChip.tone === "good"
                      ? "earn-pill--good"
                      : epsChip.tone === "bad"
                      ? "earn-pill--bad"
                      : "earn-pill--neutral"
                  }`}
                >
                  {epsChip.text}
                </div>
              )}
              {revChip?.show && (
                <div
                  className={`earn-pill ${
                    revChip.tone === "good"
                      ? "earn-pill--good"
                      : revChip.tone === "bad"
                      ? "earn-pill--bad"
                      : "earn-pill--neutral"
                  }`}
                >
                  {revChip.text}
                </div>
              )}
              {dataIncomplete && <div className="earn-pill earn-pill--neutral">Data incomplete</div>}
            </div>
          </div>

          {/* Stat cards */}
          <div className="earn-modalBody">
            <div className="earn-statCard">
              <div className="earn-statTitle">Reported EPS</div>

              <div className="earn-statRow">
                <div className="earn-statLabel">Reported EPS (Actual)</div>
                <div className="earn-statValue">
                  {Number.isFinite(toNumberOrNull(epsAct)) ? formatEPS(epsAct) : "N/A"}
                </div>
              </div>

              <div className="earn-statRow">
                <div className="earn-statLabel">Consensus EPS (Estimate)</div>
                <div className="earn-statValue">
                  {Number.isFinite(toNumberOrNull(epsEst)) ? formatEPS(epsEst) : "N/A"}
                </div>
              </div>

              <div className="earn-statRow">
                <div className="earn-statLabel">EPS Surprise</div>
                <div className={`earn-statValue ${epsSur ? (epsSur.diff >= 0 ? "earn-pos" : "earn-neg") : ""}`}>
                  {epsSur
                    ? `${epsSur.diff >= 0 ? "+" : ""}${formatEPS(epsSur.diff)} (${(epsSur.pct * 100).toFixed(1)}%)`
                    : "N/A"}
                </div>
              </div>
            </div>

            <div className="earn-statCard">
              <div className="earn-statTitle">Reported Revenue</div>

              <div className="earn-statRow">
                <div className="earn-statLabel">Total Revenue (Actual)</div>
                <div className="earn-statValue">
                  {Number.isFinite(toNumberOrNull(revAct)) ? `$${formatMoneyCompact(revAct)}` : "N/A"}
                </div>
              </div>

              <div className="earn-statRow">
                <div className="earn-statLabel">Consensus Revenue (Estimate)</div>
                <div className="earn-statValue">
                  {Number.isFinite(toNumberOrNull(revEst)) ? `$${formatMoneyCompact(revEst)}` : "N/A"}
                </div>
              </div>

              <div className="earn-statRow">
                <div className="earn-statLabel">Revenue Surprise</div>
                <div className={`earn-statValue ${revSur ? (revSur.diff >= 0 ? "earn-pos" : "earn-neg") : ""}`}>
                  {revSur
                    ? `${revSur.diff >= 0 ? "+" : "-"}$${formatMoneySignedCompact(revSur.diff)
                        .replace("+", "")
                        .replace("-", "")} (${(revSur.pct * 100).toFixed(1)}%)`
                    : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const weekTitle = `Week of ${fmtYMD(weekStart)}`;

  const canGoPrev = weekOffset > MIN_WEEK;
  const canGoNext = weekOffset < MAX_WEEK;

  return (
    <div className="overview">
      <div className="summary-card market-tone">
        <h3>{bannerTitle}</h3>
        <p>{bannerText}</p>
      </div>

      <section className="overview-block">
        <div
          className="block-header"
          style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}
        >
          <div>
            <h2>Earnings Calendar</h2>
            <p>One week at a time (Mon–Fri). Use arrows to move week-by-week. Click a tile for details.</p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              className="modal-button"
              style={{
                borderRadius: 12,
                padding: "10px 14px",
                background: canGoPrev ? "var(--accent-primary)" : "rgba(148,163,184,0.35)",
                cursor: canGoPrev ? "pointer" : "not-allowed",
              }}
              onClick={() => canGoPrev && setWeekOffset((x) => x - 1)}
              title="Previous week"
              disabled={!canGoPrev}
            >
              ←
            </button>

            <button
              type="button"
              className="modal-button"
              style={{
                borderRadius: 12,
                padding: "10px 14px",
                background: canGoNext ? "var(--accent-primary)" : "rgba(148,163,184,0.35)",
                cursor: canGoNext ? "pointer" : "not-allowed",
              }}
              onClick={() => canGoNext && setWeekOffset((x) => x + 1)}
              title="Next week"
              disabled={!canGoNext}
            >
              →
            </button>
          </div>
        </div>

        <div className="chart-card" style={{ padding: 14 }}>
          <div className="chart-title" style={{ marginBottom: 10 }}>
            {weekTitle}
          </div>

          <div className="earnings-week2">
            {weekDays.map((d) => (
              <DayColumn key={d} ymd={d} />
            ))}
          </div>
        </div>

        <div className="fun-fact" style={{ marginTop: 18 }}>
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              - <b>EPS (Earnings Per Share)</b> = Net Income ÷ Shares Outstanding
            </p>
            <p>
              - <b>EPS Surprise %</b> = (Actual EPS − Est. EPS) ÷ |Est. EPS| × 100
            </p>
            <p>
              - <b>Revenue (Sales)</b> = Total money from products/services (top line)
            </p>
            <p>
              - <b>Revenue Surprise %</b> = (Actual Rev − Est. Rev) ÷ |Est. Rev| × 100
            </p>
            <p>
              Earnings are a company’s quarterly “report card.” Stocks often move based on surprises vs consensus
              estimates and management guidance. Even a “beat” can drop if guidance is weak (and vice versa). Mega-cap
              earnings can shift the S&amp;P 500/Nasdaq and ripple into the whole sector. Common drivers: guidance,
              margins, user growth, demand trends, and AI/capex comments (for tech).
            </p>
          </div>
        </div>
      </section>

      <Modal row={activeModal} />
    </div>
  );
};

export default EarningsSection;
